/**
 * News · Fuentes — catálogo operativo de feeds RSS.
 *
 * El catálogo (url/name/publisher) vive en código (`packages/news-ingest`);
 * acá sólo se editan las dos columnas operativas: `enabled` (toggle) y
 * `fetch_cadence_minutes` (inline). Muestra el estado del último fetch
 * (last_status / last_fetched_at / last_error) para diagnosticar feeds caídos.
 *
 * Cada PATCH refetcha la lista (la fuente de verdad del estado es el backend).
 */
import { useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchNewsSources,
  patchNewsSource,
  type NewsSource,
  type NewsSourcePatch,
} from '../../api/admin/news.js';
import { AdminApiError } from '../../api/admin/_core.js';
import { relativeTime } from '../../lib/format.js';

export function SourcesPage(): ReactElement {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ['admin', 'news', 'sources'], queryFn: fetchNewsSources });
  const [toast, setToast] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const mutation = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: NewsSourcePatch }) =>
      patchNewsSource(id, patch),
    onSuccess: (res) => {
      setToast({ kind: 'ok', text: `Fuente "${res.source.slug}" actualizada` });
      void qc.invalidateQueries({ queryKey: ['admin', 'news', 'sources'] });
    },
    onError: (err: unknown) => {
      const msg = err instanceof AdminApiError ? err.message : String(err);
      setToast({ kind: 'err', text: msg });
    },
  });

  const sources = query.data?.sources ?? [];
  const enabledCount = sources.filter((s) => s.enabled).length;

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">News · Fuentes</h1>
          <p className="page-sub">
            Catálogo operativo de feeds. El catálogo (URL/nombre) vive en código; acá editás
            sólo <strong>enabled</strong> y <strong>cadencia</strong>.
            {sources.length > 0 ? (
              <>
                {' '}·{' '}
                <span className="tt-tert mono">
                  {enabledCount}/{sources.length} habilitadas
                </span>
              </>
            ) : null}
          </p>
        </div>
        {toast ? (
          <span
            className={toast.kind === 'ok' ? 'config-toast ok' : 'config-toast err'}
            role="status"
          >
            {toast.text}
          </span>
        ) : null}
      </div>

      {query.isPending ? (
        <div className="loading">Cargando…</div>
      ) : query.isError ? (
        <div className="loading">Error: {query.error.message}</div>
      ) : sources.length === 0 ? (
        <section className="card">
          <p className="empty">Sin fuentes. Corré el seed del catálogo.</p>
        </section>
      ) : (
        <section className="card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Fuente</th>
                <th>Nivel / Categoría</th>
                <th className="r">Cadencia (min)</th>
                <th>Último fetch</th>
                <th>Estado</th>
                <th className="r">Habilitada</th>
              </tr>
            </thead>
            <tbody>
              {sources.map((s) => (
                <SourceRow
                  key={s.id}
                  source={s}
                  disabled={mutation.isPending}
                  onPatch={(patch) => mutation.mutate({ id: s.id, patch })}
                />
              ))}
            </tbody>
          </table>
        </section>
      )}
    </>
  );
}

interface SourceRowProps {
  source: NewsSource;
  disabled: boolean;
  onPatch: (patch: NewsSourcePatch) => void;
}

function SourceRow({ source, disabled, onPatch }: SourceRowProps): ReactElement {
  // Draft local de la cadencia: se confirma con Enter / blur. Evita un PATCH
  // por cada keystroke.
  const [cadence, setCadence] = useState(String(source.fetchCadenceMinutes));
  const dirty = cadence.trim() !== String(source.fetchCadenceMinutes);

  const commitCadence = (): void => {
    const n = Number.parseInt(cadence, 10);
    if (!Number.isFinite(n) || n <= 0) {
      setCadence(String(source.fetchCadenceMinutes));
      return;
    }
    if (n === source.fetchCadenceMinutes) return;
    onPatch({ fetchCadenceMinutes: n });
  };

  return (
    <tr className={source.enabled ? '' : 'pricing-inactive'}>
      <td>
        <div className="flex col gap-2">
          <strong>{source.name}</strong>
          <span className="tt-tert mono" style={{ fontSize: 'var(--fs-xs)' }}>
            {source.slug}
            {source.publisher ? ` · ${source.publisher}` : ''}
          </span>
          {source.homepageUrl ? (
            <a
              href={source.homepageUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="tt-tert"
              style={{ fontSize: 'var(--fs-xs)' }}
            >
              {source.homepageUrl}
            </a>
          ) : null}
        </div>
      </td>
      <td className="tt-sec">
        <span className="mono">{source.lang}</span>
        {source.cefrHint ? ` · ${source.cefrHint}` : ''}
        {source.categoryHint ? (
          <div className="tt-tert" style={{ fontSize: 'var(--fs-xs)' }}>{source.categoryHint}</div>
        ) : null}
      </td>
      <td className="r">
        <input
          type="number"
          min={1}
          value={cadence}
          disabled={disabled}
          onChange={(e) => setCadence(e.target.value)}
          onBlur={commitCadence}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
          }}
          aria-label={`Cadencia de ${source.name} en minutos`}
          className="config-input"
          style={{ width: 88, textAlign: 'right' }}
        />
        {dirty ? <span className="config-dirty-dot" aria-hidden="true" /> : null}
      </td>
      <td className="tt-tert">{relativeTime(source.lastFetchedAt)}</td>
      <td>
        <SourceStatus source={source} />
      </td>
      <td className="r">
        <label className="config-field-toggle" title={source.enabled ? 'Deshabilitar' : 'Habilitar'}>
          <input
            type="checkbox"
            checked={source.enabled}
            disabled={disabled}
            onChange={(e) => onPatch({ enabled: e.target.checked })}
            aria-label={`Habilitar fuente ${source.name}`}
          />
          <span className="config-toggle-track">
            <span className="config-toggle-thumb" />
          </span>
        </label>
      </td>
    </tr>
  );
}

function SourceStatus({ source }: { source: NewsSource }): ReactElement {
  if (source.lastStatus === 'ok') {
    return <span className="badge b-ok">ok</span>;
  }
  if (source.lastStatus === 'error') {
    return (
      <span className="badge b-error" title={source.lastError ?? 'error'}>
        error
      </span>
    );
  }
  return <span className="muted">— sin fetch</span>;
}
